/**
 * The clock's authoring rules — D153.
 *
 * @remarks
 * Three rules, the dead-content law applied to a countdown:
 *
 * - Every variant condition resolves like every condition in the pack.
 * - A countdown's `eventId` must be declared AND scheduled by some effect,
 *   somewhere — a number counting toward an event nothing ever queues would
 *   never appear, and the pace beside it would look like a clock with no
 *   face. Read from `everyEffectOf`, the D71 rule: every effect source.
 * - D244: a countdown that reads a COUNTER must name a declared one that
 *   something moves — the same rule, for the countdown's other face.
 * - A clock that ships on must disclose itself; that one is the schema's own
 *   refinement (`packPacingSchema`), because it needs no other declaration
 *   to judge.
 *
 * @packageDocumentation
 */

import type { ConditionalPace, ContentPackDefinition, PaceValue } from '@orpheus/content-schema';

import { counterMovesOf } from './counter-validation.js';
import { everyEffectOf } from './effect-sources.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, declaredIdSetsOf } from './reference-walkers.js';
import { brokenReference } from './semantic-validation.js';

/** Every pace value in a declaration, with its path. */
const paceValuesOf = (declaration: ConditionalPace, path: string): readonly (readonly [string, PaceValue])[] =>
	typeof declaration === 'string' || !('variants' in declaration)
		? [[path, declaration]]
		: [
			[`${path}.fallback`, declaration.fallback],
			...declaration.variants.map((variant, variantIndex): readonly [string, PaceValue] => [
				`${path}.variants[${String(variantIndex)}].pace`,
				variant.pace,
			]),
		];

/**
 * Validate the pack's pacing block.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validatePacingAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const { pacing } = contentPack;

	if (pacing === undefined) {
		return [];
	}

	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);
	const movedCounterIds = new Set(counterMovesOf(contentPack).keys());
	const scheduledEventIds = new Set(
		everyEffectOf(contentPack).flatMap((effect) => (effect.type === 'schedule_event' ? [effect.eventId] : [])),
	);

	if (typeof pacing.pace !== 'string' && 'variants' in pacing.pace) {
		pacing.pace.variants.forEach((variant, variantIndex) => {
			checkCondition(variant.condition, `pacing.pace.variants[${String(variantIndex)}].condition`, declared, failures);
		});
	}

	paceValuesOf(pacing.pace, 'pacing.pace').forEach(([path, pace]) => {
		if (pace === 'none' || pace.countdown === undefined) {
			return;
		}

		// D244: a countdown that reads a counter needs a declared counter, and one something can move — a number that never changes is a picture of a clock.
		if ('counterId' in pace.countdown) {
			const { counterId } = pace.countdown;

			if (!declared.counters.has(counterId)) {
				failures.push(brokenReference(`${path}.countdown.counterId`, counterId, 'the countdown reads an undeclared counter'));
			} else if (!movedCounterIds.has(counterId)) {
				failures.push(brokenReference(`${path}.countdown.counterId`, counterId, 'the countdown reads a counter nothing ever moves — the number would never change'));
			}

			return;
		}

		const { eventId } = pace.countdown;

		if (!declared.eventIds.has(eventId)) {
			failures.push(brokenReference(`${path}.countdown.eventId`, eventId, 'the countdown names an undeclared event'));
		} else if (!scheduledEventIds.has(eventId)) {
			failures.push(
				brokenReference(
					`${path}.countdown.eventId`,
					eventId,
					'the countdown counts toward an event no effect ever schedules — the number would never appear',
				),
			);
		}
	});

	return failures;
};
